import { useMemo, useState } from "react";
import {
  appFeesSummary,
  categoryShare,
  deliveryFeesSummary,
  ratingsBreakdown,
  reportSummary,
  salesMonth,
  salesToday,
  salesWeek,
  topProducts,
} from "@/mocks/reports";
import { formatNumber, formatSAR } from "@/utils/format";
import { downloadCsv } from "@/utils/csv";
import AppFeesCard from "./components/AppFeesCard";
import DeliveryFeesCard from "./components/DeliveryFeesCard";
import SalesChart from "./components/SalesChart";
import TopProducts from "./components/TopProducts";
import { CategoryShareCard, RatingsBreakdown } from "./components/RatingsBreakdown";

type Range = "today" | "week" | "month";

const RANGE_LABELS: { key: Range; label: string }[] = [
  { key: "today", label: "اليوم" },
  { key: "week", label: "هذا الأسبوع" },
  { key: "month", label: "هذا الشهر" },
];

const RANGE_FILE: Record<Range, string> = {
  today: "today",
  week: "week",
  month: "month",
};

export default function ReportsPage() {
  const [range, setRange] = useState<Range>("week");
  const [exported, setExported] = useState<number | null>(null);

  const points = useMemo(() => {
    if (range === "today") return salesToday;
    if (range === "month") return salesMonth;
    return salesWeek;
  }, [range]);

  const summary = reportSummary.range[range];
  const appFees = appFeesSummary[range];
  const deliveryFees = deliveryFeesSummary[range];
  const ratingTotal = ratingsBreakdown.reduce(
    (sum, bucket) => sum + bucket.count,
    0,
  );

  const handleExport = () => {
    const rangeLabel = RANGE_LABELS.find((item) => item.key === range)?.label ?? "";
    const headers = ["القسم", "البند", "القيمة"];
    const rows: (string | number)[][] = [
      ["الفترة", "النطاق الزمني", rangeLabel],
      ["المؤشرات", "إجمالي المبيعات (ر.س)", summary.sales.toFixed(2)],
      ["المؤشرات", "عدد الطلبات", summary.orders],
      ["المؤشرات", "متوسط قيمة الطلب (ر.س)", summary.avg.toFixed(2)],
      ["المؤشرات", "نسبة النمو", `${summary.growth}%`],
      ["المؤشرات", "متوسط تقييم العملاء", "4.3 / 5"],
      ["المؤشرات", "عدد التقييمات", ratingTotal],
      ["رسوم التطبيق", "الإجمالي (يتحمّلها العميل) (ر.س)", appFees.total.toFixed(2)],
      ["رسوم التطبيق", "عدد طلبات التطبيق", appFees.orders],
      ["رسوم التطبيق", "الرسوم لكل طلب (ر.س)", appFees.avg.toFixed(2)],
      [
        "رسوم التطبيق",
        `محصّلة إلكترونيًا لصالح المنصة (${formatNumber(
          appFees.electronicOrders,
        )} طلب · لا تُخصم من مستحقات المحل)`,
        appFees.electronic.toFixed(2),
      ],
      [
        "رسوم التطبيق",
        `مضافة إلى رسوم التطبيق المستحقة لطلبات الكاش (${formatNumber(
          appFees.cashOrders,
        )} طلب · حصّلها المحل من العميل)`,
        appFees.cash.toFixed(2),
      ],
      ...appFees.branches.map((item) => [
        "رسوم التطبيق",
        item.name,
        item.amount.toFixed(2),
      ]),
      ["رسوم التوصيل", "الإجمالي (ر.س)", deliveryFees.total.toFixed(2)],
      ["رسوم التوصيل", "عدد طلبات التوصيل", deliveryFees.orders],
      ["رسوم التوصيل", "متوسط الرسوم للطلب (ر.س)", deliveryFees.avg.toFixed(2)],
      ...deliveryFees.branches.map((item) => [
        "رسوم التوصيل",
        `${item.name} (${item.enabled ? "مفعّل" : "غير مفعّل"})`,
        item.amount.toFixed(2),
      ]),
      ...topProducts.map((item) => [
        "أفضل المنتجات",
        item.name,
        `${item.sold} وحدة · ${item.revenue.toFixed(2)} ر.س`,
      ]),
    ];
    downloadCsv(`reports-summary-${RANGE_FILE[range]}.csv`, headers, rows);
    setExported(rows.length);
    window.setTimeout(() => setExported(null), 2500);
  };

  const kpis = [
    {
      label: "إجمالي المبيعات",
      value: formatSAR(summary.sales),
      icon: "ri-money-dollar-circle-line",
      tone: "bg-primary-100 text-primary-700",
      change: `+${summary.growth}%`,
    },
    {
      label: "عدد الطلبات",
      value: formatNumber(summary.orders),
      icon: "ri-shopping-bag-3-line",
      tone: "bg-accent-100 text-accent-800",
      change: `+${Math.max(summary.growth - 2, 1)}%`,
    },
    {
      label: "متوسط قيمة الطلب",
      value: formatSAR(summary.avg),
      icon: "ri-scales-3-line",
      tone: "bg-secondary-200 text-secondary-800",
      change: `+3%`,
    },
    {
      label: "تقييم العملاء",
      value: "4.3 / 5",
      icon: "ri-star-line",
      tone: "bg-accent-100 text-accent-800",
      change: `${formatNumber(ratingTotal)} تقييم`,
    },
  ];

  return (
    <div className="space-y-4">
      <section className="yc-fade-up flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-white rounded-card border border-background-200 shadow-card px-5 py-4">
        <div>
          <h2 className="text-base font-bold text-foreground-950">
            أداء المحل
          </h2>
          <p className="mt-0.5 text-xs text-foreground-500">
            ملخص شامل لمبيعاتك وطلباتك وتقييمات عملائك
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="inline-flex rounded-full bg-background-100 p-1">
            {RANGE_LABELS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setRange(item.key)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  range === item.key
                    ? "bg-primary-500 text-white"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-2 h-9 rounded-full bg-primary-500 px-4 text-xs font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-2-line"></i>
              تصدير CSV
            </button>
            {exported !== null && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700">
                <i className="ri-checkbox-circle-line"></i>
                تم تصدير الملخّص
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((item) => (
          <div
            key={item.label}
            className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5"
          >
            <div className="flex items-center justify-between">
              <span
                className={`w-9 h-9 flex items-center justify-center rounded-full text-base ${item.tone}`}
              >
                <i className={item.icon}></i>
              </span>
              <span className="rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-semibold text-primary-700">
                {item.change}
              </span>
            </div>
            <p className="mt-4 text-2xl font-bold text-foreground-950">
              {item.value}
            </p>
            <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
          </div>
        ))}
      </div>

      <AppFeesCard data={appFees} range={range} />

      <DeliveryFeesCard data={deliveryFees} range={range} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <SalesChart points={points} />
        </div>
        <TopProducts products={topProducts} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <RatingsBreakdown
          buckets={ratingsBreakdown}
          average={4.3}
          total={ratingTotal}
        />
        <CategoryShareCard shares={categoryShare} />
      </div>
    </div>
  );
}